import React from "react";
import SidePanel from "./SidePanel";
import { Icon, D, VehiclePhoto } from "../search/icons";
import CompanyLogo from "../search/CompanyLogo";
import useCompanies from "../search/useCompanies";
import { ContactLine, DIcon, DocsSection, InfoBox, VerificationBox, Verified, companyDocs, phoneDigits, telHref, waHref } from "./detailParts";
import { destCities, fmtAgo, fmtFree, nf } from "../search/liveTrucks";
import { LOAD_TYPES, ORDER_TYPES, bodyTypeName, cityOf, codeSign, currencyLabel, money, fmtDateShort, shortId } from "../loads/common";

const ROLE_TYPE = { DRIVER: "Перевозчик", COMPANY: "Перевозчик", DISPATCHER: "Экспедитор", CLIENT: "Грузовладелец" };
export const roleType = (role) => ROLE_TYPE[role] || null;

const PAYMENT_METHOD = { CASH: "Наличные", BANKING: "Безналичный расчёт", CARD: "Банковская карта", COMBINED: "Комбинированный", ANY: "Любой" };
const PAYMENT_FORM = { WHEN_LOADING: "При погрузке", WHEN_UNLOADING: "При разгрузке", PREPAYMENT: "Предоплата" };

const STATUS_TEXT = { ACTIVE: "Ищет груз", BUSY: "В рейсе", INACTIVE: "Неактивна" };

/** Company / driver block (building icon instead of an abbreviation) used in cards and notifications. */
export function PartyCard({ name, company, type, phone, sub, icon = D.building }) {
  return (
    <div className="side-card">
      <span className="badge-icon">
        <Icon d={icon} />
      </span>
      <div className="grow">
        <strong>{company || name || "—"}</strong>
        <small>{[company && name && company !== name ? name : null, type, sub].filter(Boolean).join(" · ")}</small>
        {phone ? (
          <small>
            <a href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}>{phone}</a>
          </small>
        ) : null}
      </div>
    </div>
  );
}

/** Avatar of a company profile (its picture) or the stock company icon. */
const Avatar = ({ co }) => <CompanyLogo src={co?.profile_images} className="dt-avatar" />;

/** Contact lines + «Позвонить» / «Написать в WhatsApp» (real company phone, e-mail, address). */
function ContactBlock({ legal, city, phone, email }) {
  return (
    <>
      <div className="dt-lines">
        <ContactLine icon="doc">{legal}</ContactLine>
        <ContactLine icon="pin">{city}</ContactLine>
        {phone ? (
          <>
            <ContactLine href={telHref(phone)} icon="phone">
              {phone}
            </ContactLine>
            {waHref(phone) ? (
              <ContactLine external href={waHref(phone)} icon="chat">
                {phone}
              </ContactLine>
            ) : null}
          </>
        ) : (
          <ContactLine icon="phone">Телефон не указан</ContactLine>
        )}
        <ContactLine href={`mailto:${email}`} icon="mail">
          {email}
        </ContactLine>
      </div>
    </>
  );
}

function ContactActions({ phone }) {
  return (
    <div className="dt-actions">
      {phone ? (
        <>
          <a className="button" href={telHref(phone)}>
            <DIcon name="phone" />
            Позвонить
          </a>
          {waHref(phone) ? (
            <a className="button dt-wa" href={waHref(phone)} rel="noopener noreferrer" target="_blank">
              <DIcon name="chat" />
              Написать в WhatsApp
            </a>
          ) : null}
        </>
      ) : (
        <button className="button" disabled type="button">
          Телефон не указан
        </button>
      )}
    </div>
  );
}

/** «Детали транспорта»: a live truck from `GET /m/trucks` + the owner's company profile. */
export function TruckDetailsPanel({ truck: t, sent, onRequest, onClose }) {
  const { byOwner } = useCompanies();
  const co = byOwner[String(t.driver_id)] || null;
  const dest = destCities(t);
  const sign = codeSign(t.currency) || "сом";
  const company = co?.company_name || t.company_name;
  const person = t.driver_name;
  const phone = co?.phone || null;
  const spec = [t.body_type_name, t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null, t.max_load_volume != null ? `${nf(t.max_load_volume)} м³` : null].filter(Boolean).join(" • ");
  return (
    <SidePanel
      className="details"
      title="Детали транспорта"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="button dt-ghost" disabled title="Чаты скоро появятся" type="button">
            <DIcon name="chat" />
            Написать перевозчику
          </button>
          {onRequest ? (
            <button className="button primary" disabled={!!sent || t.status === "BUSY"} onClick={onRequest} type="button">
              <DIcon name="truck" />
              {sent ? "Заявка отправлена" : "Отправить заявку"}
            </button>
          ) : (
            <button className="button" onClick={onClose} type="button">
              Закрыть
            </button>
          )}
        </>
      }
    >
      <div className="dt-vehicle">
        <VehiclePhoto label={t.mark || "Машина"} category={t.category_name} />
        <div>
          <h3>{t.mark || "Машина"}</h3>
          {spec ? <p>{spec}</p> : null}
          {STATUS_TEXT[t.status] ? <span className={`dt-status ${String(t.status || "").toLowerCase()}`}>{STATUS_TEXT[t.status]}</span> : null}
        </div>
      </div>

      <div className="dt-grid">
        <section>
          <h3>Перевозчик</h3>
          <div className="dt-party">
            <Avatar co={co} name={company || person} />
            <div>
              <strong>
                {company || person || "—"}
                {co?.verified ? <Verified /> : null}
              </strong>
              <p>{[company && person && company !== person ? person : null, roleType(t.owner_role), fmtAgo(t.confirmed_minutes_ago) ? `на связи ${fmtAgo(t.confirmed_minutes_ago)}` : null].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <ContactBlock city={co?.address || co?.city} email={co?.email} legal={company} phone={phone} />
        </section>
        <VerificationBox company={co} phone={phone} />
      </div>
      <ContactActions phone={phone} />
      <DocsSection docs={companyDocs(co)} />

      <InfoBox
        icon="truck"
        left={[
          ["Категория транспорта", t.category_name],
          ["Марка / модель", t.mark],
          ["Госномер", t.government_number],
          ["Тип кузова", t.body_type_name],
        ]}
        right={[
          ["Грузоподъёмность", t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null],
          ["Объём кузова", t.max_load_volume != null ? `${nf(t.max_load_volume)} м³` : null],
          ["Год выпуска", t.year],
          ["Прицеп", t.is_trailer ? "есть" : null],
        ]}
        title="Характеристики машины"
      />
      <InfoBox
        icon="pin"
        left={[
          ["Текущая локация", t.city],
          ["Когда свободен", t.status === "BUSY" ? null : fmtFree(t)],
        ]}
        right={[
          ["Радиус подачи", t.radius_km != null ? `${t.radius_km} км` : null],
          ["Куда ищет", dest.length ? dest.join(", ") : "Любое направление"],
        ]}
        title="Маршрут и доступность"
      />
      <section className="dt-price">
        <h3>{t.min_price != null ? "Ставка от" : "Ставка"}</h3>
        <strong>{t.min_price != null ? `${nf(t.min_price)} ${sign}` : "по договорённости"}</strong>
      </section>
    </SidePanel>
  );
}

/** Compact load summary: cargo, route, weight / volume, date, rate (used by details and the offer flow). */
export function LoadSummary({ order: o }) {
  const w = o.item?.weight;
  const v = o.item?.volume;
  const dateFrom = fmtDateShort(o.from_date);
  const dateTo = o.to_date && o.to_date !== o.from_date ? fmtDateShort(o.to_date) : null;
  const price = money(o.price, o.currency);
  const facts = [
    ["Маршрут", `${cityOf(o.from_address)} → ${cityOf(o.to_address)}`, o.distance],
    w != null || v != null ? ["Вес / Объём", [w != null ? `${nf(w)} т` : null, v != null ? `${nf(v)} м³` : null].filter(Boolean).join(" / "), null] : null,
    dateFrom && dateFrom !== "—" ? ["Дата погрузки", dateFrom, dateTo ? `до ${dateTo}` : null] : null,
    ["Ставка", price || "Торг", price && w ? `${nf(Math.round(o.price / w))} ${currencyLabel(o.currency)}/т` : null],
  ].filter(Boolean);
  return (
    <>
      <div className="cf-summary">
        <span className="cf-cargo-icon">
          <DIcon name="doc" />
        </span>
        <div>
          <strong>{ORDER_TYPES[o.item?.type] || "Груз"}</strong>
          <small>
            {o.from_address || "—"} → {o.to_address || "—"}
          </small>
        </div>
      </div>
      <dl className="cf-facts">
        {facts.map(([k, val, sub]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{val}</dd>
            {sub ? <small>{sub}</small> : null}
          </div>
        ))}
      </dl>
    </>
  );
}

const loadMethods = (o) => (Array.isArray(o.load_type) ? o.load_type.map((x) => LOAD_TYPES[Number(x)]).filter(Boolean).join(", ") : "");

/** «Детали груза»: an order from Django `/orders/` (own, someone's, or a notification's). */
export function LoadDetailsPanel({ order: o, onClose, footer, children, title }) {
  const { byOwner } = useCompanies();
  const co = o.owner != null ? byOwner[String(o.owner)] || null : null;
  const c = o.contacts || {};
  const company = c.company_name || co?.company_name;
  const person = c.contact_name;
  const phone = c.phone_number || co?.phone || null;
  const temp = o.temperature ? `Температурный режим${o.temperature_min != null || o.temperature_max != null ? `: ${[o.temperature_min, o.temperature_max].filter((x) => x != null).join(" … ")} °C` : ""}` : null;
  const special = [o.item?.additional_requirements, temp].filter(Boolean).join(". ");
  const pay = [PAYMENT_METHOD[o.payment_method] || o.payment_method, PAYMENT_FORM[o.payment_form] || o.payment_form, o.nds ? "с НДС" : null].filter(Boolean).join(", ");
  return (
    <SidePanel
      className="details"
      title={title || "Детали груза"}
      subtitle={o.uuid ? `Груз #${shortId(o)}` : ""}
      onClose={onClose}
      wide
      footer={
        footer || (
          <button className="button" onClick={onClose} type="button">
            Закрыть
          </button>
        )
      }
    >
      {o.from_address || o.to_address ? <LoadSummary order={o} /> : null}
      {o.from_address || o.to_address ? (
        <InfoBox
          left={[
            ["Тип кузова", bodyTypeName(o.transport_body_type)],
            ["Способ загрузки", loadMethods(o)],
            ["Условия оплаты", pay],
            ["Особые требования", special],
          ]}
          right={[
            ["Категория груза", ORDER_TYPES[o.item?.type]],
            ["Описание груза", o.comment],
          ]}
          title="Дополнительная информация"
        />
      ) : null}

      {company || person || phone || co ? (
        <section className="dt-box">
          <h3>Грузовладелец</h3>
          <div className="dt-party">
            <Avatar co={co} name={company || person} />
            <div>
              <strong>
                {company || person || "—"}
                {co?.verified ? <Verified /> : null}
              </strong>
              {o.owner_type ? <span className="dt-source">{o.owner_type}</span> : null}
              {company && person && company !== person ? <p className="dt-person">{person}</p> : null}
            </div>
          </div>
          <div className="dt-lines">
            {phone ? (
              <>
                <ContactLine href={telHref(phone)} icon="phone">
                  {phone}
                </ContactLine>
                {phoneDigits(phone).length >= 7 ? (
                  <ContactLine external href={waHref(phone)} icon="chat">
                    {phone}
                  </ContactLine>
                ) : null}
              </>
            ) : (
              <ContactLine icon="phone">Телефон не указан</ContactLine>
            )}
            <ContactLine href={`mailto:${co?.email}`} icon="mail">
              {co?.email}
            </ContactLine>
            <ContactLine icon="pin">{co?.address || co?.city}</ContactLine>
          </div>
        </section>
      ) : null}
      <VerificationBox company={co} phone={phone} />
      {co ? <DocsSection docs={companyDocs(co)} /> : null}
      {children}
    </SidePanel>
  );
}
